import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Swords, Trash2 } from 'lucide-react';
import { Heading } from '../../layout';
import { FormDrawer } from '../../forms';
import { useUIColors } from '../../../hooks/useTheme';
import { useSectionAccess } from '../../../hooks/useSectionAccess';
import { additivesCatalogAPI } from '../../../services/additivesCatalogAPI';
import ProductionDataTable from '../../placeholders/ProductionDataTable';
import { collectApplicationTypes } from './productApplications';

const tableCell = (children, align = 'left') => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align }}>{children}</td>
);

const TAB_STORAGE_KEY = 'sqmt_competitors_tab';
const KIND_PRODUCT = 'producto';
const KIND_COMPANY = 'empresa';

const emptyRecord = (kind) => ({
  kind,
  name: '',
  product: '',
  family: '',
  country: '',
  website: '',
  summary: '',
  applications: [],
});

const chipStyle = (active, ui) => ({
  height: 30,
  padding: '0 12px',
  borderRadius: 999,
  border: `1px solid ${active ? ui.interactive || '#16a34a' : ui.border}`,
  background: active ? ui.interactive || '#16a34a' : 'transparent',
  color: active ? ui.textOnAccent || '#fff' : ui.text,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
});

const ApplicationPicker = ({ value = [], options = [], onChange, ui }) => {
  const [draft, setDraft] = useState('');
  const selected = Array.isArray(value) ? value : [];

  const toggle = (item) => {
    onChange(selected.includes(item) ? selected.filter((entry) => entry !== item) : [...selected, item]);
  };

  const addDraft = () => {
    const next = draft.trim();
    if (!next) return;
    if (!selected.includes(next)) onChange([...selected, next]);
    setDraft('');
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: ui.text }}>Aplicaciones de producto</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map((item) => {
          const active = selected.includes(item);
          return (
            <button key={item} type="button" onClick={() => toggle(item)} style={chipStyle(active, ui)}>
              {item}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addDraft();
            }
          }}
          placeholder="Otra aplicación…"
          style={{
            flex: 1,
            height: 34,
            padding: '0 10px',
            borderRadius: 8,
            border: `1px solid ${ui.inputBorder || ui.border}`,
            background: ui.inputBg || ui.panel,
            color: ui.inputText || ui.text,
            fontSize: 13,
          }}
        />
        <button type="button" onClick={addDraft} style={chipStyle(false, ui)}>
          Agregar
        </button>
      </div>
    </div>
  );
};

const normalizeKind = (item) => {
  const kind = String(item?.kind || '').trim().toLowerCase();
  if (kind === KIND_COMPANY) return KIND_COMPANY;
  if (kind === KIND_PRODUCT) return KIND_PRODUCT;
  return String(item?.product || '').trim() ? KIND_PRODUCT : KIND_COMPANY;
};

const CompetitorsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const access = useSectionAccess();
  const [tab, setTab] = useState(() => {
    try {
      return window.localStorage.getItem(TAB_STORAGE_KEY) === KIND_COMPANY ? KIND_COMPANY : KIND_PRODUCT;
    } catch {
      return KIND_PRODUCT;
    }
  });
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyRecord(KIND_PRODUCT));
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const rows = await additivesCatalogAPI.listCompetitors();
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setItems([]);
      setError(err?.message || 'No se pudieron cargar los competidores.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
      /* ignore */
    }
  }, [tab]);

  const normalized = useMemo(
    () => items.map((item) => ({ ...item, kind: normalizeKind(item) })),
    [items]
  );

  const companies = useMemo(
    () => normalized.filter((item) => item.kind === KIND_COMPANY),
    [normalized]
  );

  const products = useMemo(
    () => normalized.filter((item) => item.kind === KIND_PRODUCT),
    [normalized]
  );

  const productCountByCompany = useMemo(() => {
    const map = new Map();
    products.forEach((item) => {
      const key = String(item.name || '').trim().toLowerCase();
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [products]);

  const productRows = useMemo(
    () =>
      products.map((item) => ({
        ...item,
        applicationsLabel: (item.applications || []).join(' '),
      })),
    [products]
  );

  const companyRows = useMemo(
    () =>
      companies.map((item) => {
        const key = String(item.name || '').trim().toLowerCase();
        return {
          ...item,
          applicationsLabel: (item.applications || []).join(' '),
          productCount: productCountByCompany.get(key) || 0,
        };
      }),
    [companies, productCountByCompany]
  );

  const isProducts = tab === KIND_PRODUCT;
  const tableRows = isProducts ? productRows : companyRows;

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyRecord(tab));
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (item) => {
    const kind = normalizeKind(item);
    setEditingId(item.id);
    setForm({
      kind,
      name: item.name || '',
      product: item.product || '',
      family: item.family || '',
      country: item.country || '',
      website: item.website || '',
      summary: item.summary || '',
      applications: Array.isArray(item.applications) ? item.applications : [],
    });
    setFormError('');
    setFormOpen(true);
  };

  const saveForm = async () => {
    try {
      setIsSaving(true);
      setFormError('');
      if (editingId) {
        await additivesCatalogAPI.updateCompetitor(editingId, form);
      } else {
        await additivesCatalogAPI.createCompetitor(form);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeItem = async (item) => {
    const label = item.kind === KIND_COMPANY ? item.name : item.product || item.name;
    if (!window.confirm(`¿Eliminar “${label}”?`)) return;
    try {
      await additivesCatalogAPI.removeCompetitor(item.id);
      await load();
    } catch (err) {
      window.alert(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive || '#16a34a',
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const renderApplicationChips = (apps) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {(apps || []).length ? (
        apps.map((app) => (
          <span
            key={app}
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '3px 8px',
              borderRadius: 999,
              border: `1px solid ${ui.border}`,
              color: ui.text,
            }}
          >
            {app}
          </span>
        ))
      ) : (
        <span style={{ fontSize: 12, color: ui.muted }}>Sin aplicación</span>
      )}
    </div>
  );

  const companyNames = useMemo(
    () =>
      Array.from(
        new Set(companies.map((item) => String(item.name || '').trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, 'es')),
    [companies]
  );

  const formIsProduct = form.kind === KIND_PRODUCT;
  const formSections = [
    {
      key: 'main',
      title: formIsProduct ? 'Producto competidor' : 'Empresa competidora',
      defaultOpen: true,
      fields: formIsProduct
        ? [
            { key: 'product', label: 'Producto' },
            {
              key: 'name',
              label: 'Empresa',
              type: 'custom',
              render: ({ value, onChange }) => (
                <div style={{ display: 'grid', gap: 8 }}>
                  <input
                    value={value || ''}
                    onChange={(event) => onChange(event.target.value)}
                    list="competitor-company-names"
                    placeholder="Nombre de la empresa"
                    style={{
                      height: 34,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: `1px solid ${ui.inputBorder || ui.border}`,
                      background: ui.inputBg || ui.panel,
                      color: ui.inputText || ui.text,
                      fontSize: 13,
                    }}
                  />
                  <datalist id="competitor-company-names">
                    {companyNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </div>
              ),
            },
            { key: 'family', label: 'Familia / categoría' },
            { key: 'country', label: 'País / origen' },
            { key: 'summary', label: 'Notas', type: 'textarea', rows: 4, fullWidth: true },
            {
              key: 'applications',
              type: 'custom',
              fullWidth: true,
              render: ({ value, onChange }) => (
                <ApplicationPicker
                  value={value}
                  onChange={onChange}
                  options={collectApplicationTypes([{ applications: value }, ...normalized])}
                  ui={ui}
                />
              ),
            },
          ]
        : [
            { key: 'name', label: 'Empresa' },
            { key: 'country', label: 'País / origen' },
            { key: 'website', label: 'Sitio web', fullWidth: true },
            { key: 'summary', label: 'Notas', type: 'textarea', rows: 4, fullWidth: true },
            {
              key: 'applications',
              type: 'custom',
              fullWidth: true,
              render: ({ value, onChange }) => (
                <ApplicationPicker
                  value={value}
                  onChange={onChange}
                  options={collectApplicationTypes([{ applications: value }, ...normalized])}
                  ui={ui}
                />
              ),
            },
          ],
    },
  ];

  const actionButtons = (item) =>
    tableCell(
      <div style={{ display: 'inline-flex', gap: 6 }}>
        {access.canUpdate ? (
          <button
            type="button"
            title="Editar"
            onClick={(event) => {
              event.stopPropagation();
              openEdit(item);
            }}
            style={ghostBtn}
          >
            <Pencil size={13} />
          </button>
        ) : null}
        {access.canDelete ? (
          <button
            type="button"
            title="Eliminar"
            onClick={(event) => {
              event.stopPropagation();
              removeItem(item);
            }}
            style={ghostBtn}
          >
            <Trash2 size={13} />
          </button>
        ) : null}
      </div>,
      'right'
    );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          <Swords size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Competidores
        </Heading>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: ui.muted }}>
          Empresas y productos que compiten con PhenoX®, por aplicación de mercado.
        </p>
      </div>

      {error ? <div style={{ fontSize: 13, color: '#dc2626' }}>{error}</div> : null}
      {isLoading ? <div style={{ fontSize: 13, color: ui.muted }}>Cargando competidores…</div> : null}

      {!isLoading ? (
        <ProductionDataTable
          key={tab}
          title="Competidores"
          subtitle={
            isProducts
              ? `${productRows.length} producto${productRows.length === 1 ? '' : 's'}`
              : `${companyRows.length} empresa${companyRows.length === 1 ? '' : 's'}`
          }
          rows={tableRows}
          columns={
            isProducts
              ? [
                  { key: 'Producto', sortKey: 'product' },
                  { key: 'Empresa', sortKey: 'name' },
                  { key: 'Familia', sortKey: 'family' },
                  { key: 'Aplicaciones', sortKey: 'applicationsLabel' },
                  { key: 'Origen', sortKey: 'country' },
                  { key: '', sortKey: '' },
                ]
              : [
                  { key: 'Empresa', sortKey: 'name' },
                  { key: 'Origen', sortKey: 'country' },
                  { key: 'Sitio', sortKey: 'website' },
                  { key: 'Aplicaciones', sortKey: 'applicationsLabel' },
                  { key: 'Productos', sortKey: 'productCount', align: 'right' },
                  { key: '', sortKey: '' },
                ]
          }
          defaultSortKey={isProducts ? 'product' : 'name'}
          searchPlaceholder={
            isProducts ? 'Buscar producto, empresa o familia…' : 'Buscar empresa, país o sitio…'
          }
          onRowClick={access.canUpdate ? openEdit : undefined}
          tabs={[
            { key: KIND_PRODUCT, label: 'Productos', count: productRows.length },
            { key: KIND_COMPANY, label: 'Empresas', count: companyRows.length },
          ]}
          activeTab={tab}
          onTabChange={setTab}
          action={
            access.canCreate ? (
              <button type="button" onClick={openCreate} style={primaryBtn}>
                <Plus size={14} />
                {isProducts ? 'Nuevo producto' : 'Nueva empresa'}
              </button>
            ) : null
          }
          renderCells={(item) =>
            isProducts ? (
              <>
                {tableCell(<strong>{item.product || '—'}</strong>)}
                {tableCell(item.name || '—')}
                {tableCell(item.family || '—')}
                {tableCell(renderApplicationChips(item.applications))}
                {tableCell(item.country || '—')}
                {actionButtons(item)}
              </>
            ) : (
              <>
                {tableCell(<strong>{item.name || '—'}</strong>)}
                {tableCell(item.country || '—')}
                {tableCell(
                  item.website ? (
                    <a
                      href={item.website}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      style={{ color: ui.interactive || '#16a34a' }}
                    >
                      {item.website}
                    </a>
                  ) : (
                    '—'
                  )
                )}
                {tableCell(renderApplicationChips(item.applications))}
                {tableCell(item.productCount || 0, 'right')}
                {actionButtons(item)}
              </>
            )
          }
        />
      ) : null}

      <FormDrawer
        isOpen={formOpen}
        onClose={() => !isSaving && setFormOpen(false)}
        onSave={saveForm}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title={
          editingId
            ? formIsProduct
              ? 'Editar producto'
              : 'Editar empresa'
            : formIsProduct
              ? 'Nuevo producto'
              : 'Nueva empresa'
        }
        subtitle="Catálogo PhenoX®"
        sections={formSections}
        formData={form}
        onChange={(key, value) => setForm((prev) => ({ ...prev, [key]: value }))}
        formError={formError}
        saveLabel={editingId ? 'Guardar' : 'Crear'}
        readOnly={editingId ? !access.canUpdate : !access.canCreate}
        width={560}
      />
    </div>
  );
};

export default CompetitorsWorkspace;
